"use client";

import { useCallback, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { showToast } from "@/components/ui/sonner";
import { useCreateSearch } from "@/hooks/use-create-search";
import { usePendingNqlMessage } from "@/stores/use-pending-nql-message";
import { useJdMessageMeta } from "@/stores/use-jd-message-meta";

export interface StartTalentSearchFromJobInput {
  title: string;
  description?: string | null;
}

/**
 * Build the first-chat JD seed used when launching Talent Search from a job.
 * Prefix must match the regex in assistant/user-message.tsx so the chat
 * renders the JD as a collapsible card.
 */
export function buildJobDescriptionSearchMessage(
  title: string,
  description: string,
): { message: string; meta: { source: "job"; title?: string } } {
  const prefix = title
    ? `Here is the job description for "${title}":\n\n`
    : "Here is the job description I'd like to search by:\n\n";
  return {
    message: prefix + description,
    meta: {
      source: "job",
      title: title || undefined,
    },
  };
}

/**
 * Creates an empty search, seeds the JD as the first chat message, and
 * navigates to Talent Search — same path as the job wizard complete step.
 *
 * Returns `true` when navigation was kicked off, `false` on no-op / failure.
 */
export function useStartTalentSearchFromJob(projectId: string | null) {
  const router = useRouter();
  const { createSearch, isPending } = useCreateSearch({ projectId });
  const [starting, setStarting] = useState(false);
  const pendingRef = useRef(false);

  const start = useCallback(
    async ({ title, description }: StartTalentSearchFromJobInput) => {
      if (pendingRef.current) return false;
      if (!projectId) {
        showToast.error("No project selected");
        return false;
      }

      pendingRef.current = true;
      setStarting(true);
      try {
        const data = await createSearch({});
        const { message, meta } = buildJobDescriptionSearchMessage(
          title,
          description ?? "",
        );
        useJdMessageMeta.getState().setPending(meta);
        usePendingNqlMessage.getState().set({
          kind: "text",
          searchId: data.search.id,
          message,
        });
        router.push(`/talent-search/${data.search.id}`);
        return true;
      } catch (err) {
        pendingRef.current = false;
        setStarting(false);
        showToast.error("Failed to start talent search", {
          description: err instanceof Error ? err.message : undefined,
        });
        return false;
      }
    },
    [createSearch, projectId, router],
  );

  return {
    start,
    isPending: isPending || starting,
  };
}
